export const algebra2Topic1 = {
  id: "complex-numbers",
  title: "Complex Numbers",
  standards: {
    commonCore: ["HSN-CN.A.1", "HSN-CN.A.2", "HSN-CN.C.7"],
    nctm: ["Number and Operations"],
    states: { NY: ["AII-N.CN.1"], OH: ["N.CN.1"], PA: ["CC.2.1.HS.F.6"], VA: ["AII.3"], TX: ["A2.7A"] }
  },
  explanation: `**Mr. Augustine says:** "Complex numbers are NOT complicated — they just have two parts! Once you see the pattern, they're as easy as regular arithmetic!"

**What is a Complex Number?**
A complex number has the form: **a + bi**
- **a** = the real part; **b** = the imaginary part; **i = √(−1)**, so **i² = −1**

**Powers of i (cycle every 4):**
- i¹ = i, i² = −1, i³ = −i, i⁴ = 1, then repeats!
- To find iⁿ: divide n by 4, use the remainder (0→1, 1→i, 2→−1, 3→−i)

**Adding/Subtracting:** Combine real parts, combine imaginary parts.
- (3 + 2i) + (5 + 4i) = 8 + 6i

**Multiplying:** Use FOIL, replace i² with −1.
- (2 + 3i)(4 + i) = 8 + 2i + 12i + 3i² = 8 + 14i − 3 = 5 + 14i

**Complex Conjugate:** Conjugate of (a + bi) is (a − bi).
- (a + bi)(a − bi) = a² + b² (always a real number!)

**Dividing:** Multiply top and bottom by the conjugate of the denominator.

**Modulus:** |a + bi| = √(a² + b²)`,
  practiceQuestions: [
    { question: "Simplify: (3 + 4i) + (5 − 2i)", solution: "Real: 3+5=8. Imaginary: 4i−2i=2i.\n**Answer: 8 + 2i**" },
    { question: "Simplify: (7 + 3i) − (2 + 5i)", solution: "Real: 7−2=5. Imaginary: 3i−5i=−2i.\n**Answer: 5 − 2i**" },
    { question: "Simplify: i²", solution: "By definition i²=−1.\n**Answer: −1**" },
    { question: "Simplify: i⁷", solution: "7÷4=1 remainder 3. Remainder 3 → i³=−i.\n**Answer: −i**" },
    { question: "Multiply: (2 + 3i)(1 + 4i)", solution: "FOIL: 2+8i+3i+12i²=2+11i−12=−10+11i.\n**Answer: −10 + 11i**" },
    { question: "Find the conjugate of (5 − 3i) and multiply by the original.", solution: "Conjugate=(5+3i). Product: 5²+3²=25+9=34.\n**Answer: 34**" },
    { question: "Divide: (4 + 2i) ÷ (1 − i)", solution: "Multiply by (1+i)/(1+i): Num=(4+4i+2i+2i²)=2+6i. Den=2. Result=1+3i.\n**Answer: 1 + 3i**" },
    { question: "Find |3 + 4i|.", solution: "|a+bi|=√(a²+b²)=√(9+16)=√25=5.\n**Answer: 5**" },
    { question: "Simplify: √(−49)", solution: "√(49×−1)=7i.\n**Answer: 7i**" },
    { question: "Simplify: (2i)³", solution: "8×i³=8×(−i)=−8i.\n**Answer: −8i**" },
    { question: "Solve: x² + 9 = 0", solution: "x²=−9. x=±3i.\n**Answer: x = ±3i**" },
    { question: "Simplify: (1 + i)²", solution: "1+2i+i²=1+2i−1=2i.\n**Answer: 2i**" },
    { question: "Multiply: (3 − 2i)(3 + 2i)", solution: "Conjugate pair: 3²+2²=9+4=13.\n**Answer: 13**" },
    { question: "Simplify: i¹⁰⁰", solution: "100÷4=25 remainder 0. Remainder 0 → 1.\n**Answer: 1**" },
    { question: "Divide: (6 + 3i) ÷ (2 + i)", solution: "Multiply by (2−i)/(2−i): Num=12−6i+6i−3i²=12+3=15. Den=5. 15/5=3.\n**Answer: 3**" },
    { question: "Find real and imaginary parts of: (2 + 5i) − (4 − 3i)", solution: "Real: 2−4=−2. Imaginary: 5i+3i=8i.\n**Answer: Real=−2, Imaginary=8**" },
    { question: "Simplify: i²³", solution: "23÷4=5 remainder 3. i³=−i.\n**Answer: −i**" },
    { question: "Multiply: (5 + 2i)(5 − 2i)", solution: "5²+2²=25+4=29.\n**Answer: 29**" },
    { question: "Solve: 2x² + 50 = 0", solution: "x²=−25. x=±5i.\n**Answer: x = ±5i**" },
    { question: "Simplify: (3 + i)(2 − 4i) + (1 + 2i)", solution: "FOIL: 6−12i+2i−4i²=10−10i. Add (1+2i): 11−8i.\n**Answer: 11 − 8i**" }
  ]
};

export const algebra2Topic2 = {
  id: "polynomial-functions",
  title: "Polynomial Functions",
  standards: {
    commonCore: ["HSA-APR.A.1", "HSA-APR.B.2", "HSA-APR.B.3"],
    nctm: ["Algebra"],
    states: { NY: ["AII-A.APR.1"], OH: ["A.APR.1"], PA: ["CC.2.2.HS.D.3"], VA: ["AII.7"], TX: ["A2.7B"] }
  },
  explanation: `**Mr. Augustine says:** "Polynomials are just fancy expressions with multiple terms. Once you know the rules, you can add, subtract, multiply, and factor them like a pro!"

**Degree:** The highest exponent. Degree 2 = quadratic, degree 3 = cubic.

**Adding/Subtracting:** Combine like terms (same variable, same exponent).

**Multiplying:** Multiply every term in the first by every term in the second (FOIL for binomials).

**Remainder Theorem:** When P(x) is divided by (x − a), remainder = P(a).

**Factor Theorem:** (x − a) is a factor of P(x) if and only if P(a) = 0.

**Synthetic Division:** A shortcut for dividing by (x − a).

**End Behavior:**
- Even degree, positive leading coeff → both ends UP
- Even degree, negative leading coeff → both ends DOWN
- Odd degree, positive leading coeff → left DOWN, right UP
- Odd degree, negative leading coeff → left UP, right DOWN

**Mr. Augustine's Tip:** "The degree tells you the MAXIMUM number of zeros!"`,
  practiceQuestions: [
    { question: "Add: (3x² + 5x − 2) + (x² − 3x + 7)", solution: "4x²+2x+5.\n**Answer: 4x² + 2x + 5**" },
    { question: "Subtract: (5x³ − 2x + 4) − (3x³ + x² − x + 1)", solution: "2x³−x²−x+3.\n**Answer: 2x³ − x² − x + 3**" },
    { question: "Multiply: (x + 4)(x − 3)", solution: "FOIL: x²−3x+4x−12=x²+x−12.\n**Answer: x² + x − 12**" },
    { question: "Multiply: (2x − 1)(3x² + x − 5)", solution: "6x³+2x²−10x−3x²−x+5=6x³−x²−11x+5.\n**Answer: 6x³ − x² − 11x + 5**" },
    { question: "Remainder Theorem: P(x) = x³ − 3x² + 2x − 5 divided by (x − 2).", solution: "P(2)=8−12+4−5=−5.\n**Answer: Remainder = −5**" },
    { question: "Factor Theorem: Is (x − 3) a factor of P(x) = x³ − 6x² + 11x − 6?", solution: "P(3)=27−54+33−6=0. Yes!\n**Answer: Yes, (x − 3) is a factor**" },
    { question: "Synthetic division: (x³ − 2x² − 5x + 6) ÷ (x − 3)", solution: "Coefficients 1,−2,−5,6 with 3: gives 1,1,−2,0.\n**Answer: x² + x − 2**" },
    { question: "Find all zeros of P(x) = x² − 5x + 6.", solution: "(x−2)(x−3)=0.\n**Answer: x = 2 and x = 3**" },
    { question: "Describe end behavior of f(x) = −2x⁴ + 3x − 1.", solution: "Even degree, negative leading coeff → both ends DOWN.\n**Answer: Both ends go to −∞**" },
    { question: "Factor completely: x³ − 8", solution: "Difference of cubes: (x−2)(x²+2x+4).\n**Answer: (x − 2)(x² + 2x + 4)**" },
    { question: "Find zeros of P(x) = x³ − x² − 6x.", solution: "x(x−3)(x+2)=0.\n**Answer: x = 0, 3, −2**" },
    { question: "Multiply: (x + 2)³", solution: "(x+2)²=x²+4x+4. Then ×(x+2)=x³+6x²+12x+8.\n**Answer: x³ + 6x² + 12x + 8**" },
    { question: "How many zeros does P(x) = x⁵ − 3x³ + 2x have at most?", solution: "Degree = 5.\n**Answer: At most 5 zeros**" },
    { question: "Synthetic division: (2x³ + 3x² − 11x − 6) ÷ (x + 3)", solution: "Use −3: gives 2,−3,−2,0.\n**Answer: 2x² − 3x − 2**" },
    { question: "Factor: x⁴ − 16", solution: "(x²−4)(x²+4)=(x−2)(x+2)(x²+4).\n**Answer: (x − 2)(x + 2)(x² + 4)**" },
    { question: "Remainder when P(x) = 2x⁴ − x³ + 3x − 7 is divided by (x + 1).", solution: "P(−1)=2+1−3−7=−7.\n**Answer: −7**" },
    { question: "Write a polynomial with zeros x = 1, x = −2, x = 3.", solution: "(x−1)(x+2)(x−3)=x³−2x²−5x+6.\n**Answer: x³ − 2x² − 5x + 6**" },
    { question: "End behavior of g(x) = 3x⁵ − 2x² + 1.", solution: "Odd degree, positive leading coeff → left DOWN, right UP.\n**Answer: x→−∞: g→−∞; x→+∞: g→+∞**" },
    { question: "Factor: 2x³ + 16", solution: "2(x³+8)=2(x+2)(x²−2x+4).\n**Answer: 2(x + 2)(x² − 2x + 4)**" },
    { question: "Find all real zeros of P(x) = x³ + 3x² − 4x − 12.", solution: "P(2)=0. Divide: (x−2)(x²+5x+6)=(x−2)(x+2)(x+3).\n**Answer: x = 2, −2, −3**" }
  ]
};

export const algebra2Topic3 = {
  id: "rational-functions",
  title: "Rational Functions & Expressions",
  standards: {
    commonCore: ["HSA-APR.D.6", "HSA-APR.D.7", "HSA-REI.A.2"],
    nctm: ["Algebra"],
    states: { NY: ["AII-A.APR.6"], OH: ["A.APR.6"], PA: ["CC.2.2.HS.D.6"], VA: ["AII.8"], TX: ["A2.7D"] }
  },
  explanation: `**Mr. Augustine says:** "Rational functions are just fractions with polynomials. The key is knowing where they're undefined — those spots create the asymptotes!"

**Domain:** Excludes all values where denominator = 0.

**Simplifying:** Factor numerator and denominator, cancel common factors.

**Multiplying:** Multiply numerators, multiply denominators, simplify.

**Dividing:** Flip the second fraction, then multiply.

**Adding/Subtracting:** Find LCD, rewrite each fraction, combine numerators.

**Vertical Asymptotes:** Where denominator = 0 (after simplifying).

**Horizontal Asymptotes (compare degrees n and m):**
- n < m: y = 0
- n = m: y = ratio of leading coefficients
- n > m: no horizontal asymptote

**Solving Rational Equations:** Multiply both sides by LCD, solve, CHECK for extraneous solutions!

**Mr. Augustine's Warning:** "ALWAYS check your answer — if it makes the denominator zero, it's EXTRANEOUS (fake)!"`,
  practiceQuestions: [
    { question: "Simplify: (x² − 9)/(x + 3)", solution: "(x+3)(x−3)/(x+3)=x−3.\n**Answer: x − 3**" },
    { question: "Find the domain of f(x) = (x+2)/(x²−5x+6).", solution: "(x−2)(x−3)=0 → x=2,3 excluded.\n**Answer: All reals except x=2 and x=3**" },
    { question: "Multiply: (x²−4)/(x+1) × (x+1)/(x−2)", solution: "(x+2)(x−2)(x+1)/[(x+1)(x−2)]=x+2.\n**Answer: x + 2**" },
    { question: "Divide: (x²−1)/(x+3) ÷ (x−1)/(x+3)", solution: "Flip: ×(x+3)/(x−1). (x+1)(x−1)(x+3)/[(x+3)(x−1)]=x+1.\n**Answer: x + 1**" },
    { question: "Add: 3/(x+2) + 5/(x+2)", solution: "Same denominator: 8/(x+2).\n**Answer: 8/(x+2)**" },
    { question: "Add: 2/x + 3/(x+1)", solution: "LCD=x(x+1). [2(x+1)+3x]/[x(x+1)]=(5x+2)/[x(x+1)].\n**Answer: (5x+2)/[x(x+1)]**" },
    { question: "Find vertical asymptotes of f(x) = (x+1)/(x²−4).", solution: "x²−4=0 → x=±2.\n**Answer: x = 2 and x = −2**" },
    { question: "Find horizontal asymptote of f(x) = (3x²+1)/(2x²−5).", solution: "Equal degrees: 3/2.\n**Answer: y = 3/2**" },
    { question: "Find horizontal asymptote of f(x) = (5x+1)/(x²+3).", solution: "n<m → y=0.\n**Answer: y = 0**" },
    { question: "Solve: 3/x = 12/20", solution: "Cross-multiply: 60=12x → x=5.\n**Answer: x = 5**" },
    { question: "Solve: 1/(x−2) = 3/(x+4)", solution: "Cross-multiply: x+4=3x−6 → x=5.\n**Answer: x = 5**" },
    { question: "Solve: x/(x−3) = 3/(x−3) + 2. Check for extraneous solutions.", solution: "Multiply by (x−3): x=3+2(x−3)=2x−3 → x=3. But x=3 makes denom=0!\n**Answer: No solution (extraneous)**" },
    { question: "Subtract: 5/(x+1) − 2/(x−1)", solution: "LCD=(x+1)(x−1). [5(x−1)−2(x+1)]/[(x+1)(x−1)]=(3x−7)/[(x²−1)].\n**Answer: (3x−7)/(x²−1)**" },
    { question: "Simplify: (2x²+7x+3)/(2x+1)", solution: "(2x+1)(x+3)/(2x+1)=x+3.\n**Answer: x + 3**" },
    { question: "Find all asymptotes of f(x) = (x²−1)/(x−1).", solution: "Simplifies to x+1 (hole at x=1). No asymptotes.\n**Answer: No asymptotes; hole at x=1**" },
    { question: "Solve: 2/(x+3) + 1/(x−3) = 5/(x²−9)", solution: "LCD=(x+3)(x−3). 2(x−3)+1(x+3)=5 → 3x−3=5 → x=8/3.\n**Answer: x = 8/3**" },
    { question: "Multiply: (x²+5x+6)/(x²−4) × (x−2)/(x+3)", solution: "(x+2)(x+3)/[(x+2)(x−2)] × (x−2)/(x+3)=1.\n**Answer: 1**" },
    { question: "Find the domain of g(x) = √(x−4)/(x²−9).", solution: "x≥4 (from sqrt) and x≠3 (from denom). Since x≥4, x=3 already excluded.\n**Answer: x ≥ 4**" },
    { question: "Solve: (x+1)/(x−2) = (x+3)/(x−4)", solution: "Cross-multiply: x²−3x−4=x²+x−6 → −4x=−2 → x=1/2.\n**Answer: x = 1/2**" },
    { question: "Simplify: (3x²−12)/(6x²−24x+24)", solution: "3(x+2)(x−2)/[6(x−2)²]=(x+2)/[2(x−2)].\n**Answer: (x+2)/[2(x−2)]**" }
  ]
};
